import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, MessageSquare, HelpCircle } from 'lucide-react';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [loading, setLoading] = useState(false);
  
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    message: ''
  });

  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    province: '',
    product_interest: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/contact`, contactForm);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', city: '', province: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads/inquiry`, inquiryForm);
      toast.success('Inquiry submitted! Our team will contact you shortly.');
      setInquiryForm({ name: '', email: '', phone: '', city: '', province: '', product_interest: '', budget: '', timeline: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-36 pb-16 section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-4" style={{  }}>
              Get In Touch
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions about our freeze dryers? Want a personalized quote? We're here to help.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="text-xl font-bold text-[#0a0203] mb-6" style={{  }}>
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#dbeef5] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a0203]">Email</p>
                      <a href="mailto:info@homelyph.com" className="text-gray-600 hover:text-[#003456]">
                        info@homelyph.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#dbeef5] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a0203]">Phone</p>
                      <a href="tel:+18882231435" className="text-gray-600 hover:text-[#003456]">
                        1-888-223-1435
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#dbeef5] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#0a0203]">Address</p>
                      <p className="text-gray-600">
                        3164 36th St N<br />
                        Lethbridge, Alberta<br />
                        Canada T1H 7E5
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#003456] rounded-2xl p-8 text-white">
                <HelpCircle className="h-8 w-8 mb-4" />
                <h3 className="text-xl font-bold mb-2" style={{  }}>Need Help Choosing?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Not sure which freeze dryer is right for you? Fill out our detailed inquiry form and our experts will help you decide.
                </p>
                <button
                  onClick={() => setActiveTab('inquiry')}
                  className="text-white font-medium underline underline-offset-4 hover:no-underline"
                >
                  Get a Personalized Recommendation →
                </button>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2 animate-slide-in-right">
              <div className="bg-white rounded-2xl p-8 md:p-12">
                {/* Tab Switcher */}
                <div className="flex gap-4 mb-8">
                  <button
                    onClick={() => setActiveTab('contact')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                      activeTab === 'contact'
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="contact-tab"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Quick Contact
                  </button>
                  <button
                    onClick={() => setActiveTab('inquiry')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                      activeTab === 'inquiry'
                        ? 'bg-[#003456] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="inquiry-tab"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Detailed Inquiry
                  </button>
                </div>

                {/* Contact Form */}
                {activeTab === 'contact' && (
                  <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Name *</label>
                        <Input
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="John Doe"
                          className="input-focus"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Email *</label>
                        <Input
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="john@example.com"
                          className="input-focus"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Phone (optional)</label>
                      <Input
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        className="input-focus"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">City</label>
                        <Input
                          value={contactForm.city}
                          onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                          placeholder="Toronto"
                          className="input-focus"
                          data-testid="contact-city"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Province/State</label>
                        <Input
                          value={contactForm.province}
                          onChange={(e) => setContactForm({ ...contactForm, province: e.target.value })}
                          placeholder="Ontario"
                          className="input-focus"
                          data-testid="contact-province"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Message *</label>
                      <Textarea
                        required
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder="How can we help you?"
                        rows={5}
                        className="input-focus"
                        data-testid="contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#003456] hover:bg-[#001d30] text-white py-6 btn-scale"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      ) : (
                        <>
                          Send Message <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}

                {/* Inquiry Form */}
                {activeTab === 'inquiry' && (
                  <form onSubmit={handleInquirySubmit} className="space-y-6" data-testid="inquiry-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Name *</label>
                        <Input
                          required
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                          placeholder="John Doe"
                          className="input-focus"
                          data-testid="inquiry-name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Email *</label>
                        <Input
                          type="email"
                          required
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                          placeholder="john@example.com"
                          className="input-focus"
                          data-testid="inquiry-email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Phone *</label>
                      <Input
                        required
                        value={inquiryForm.phone}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        className="input-focus"
                        data-testid="inquiry-phone"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">City</label>
                        <Input
                          value={inquiryForm.city}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, city: e.target.value })}
                          placeholder="Toronto"
                          className="input-focus"
                          data-testid="inquiry-city"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Province/State</label>
                        <Input
                          value={inquiryForm.province}
                          onChange={(e) => setInquiryForm({ ...inquiryForm, province: e.target.value })}
                          placeholder="Ontario"
                          className="input-focus"
                          data-testid="inquiry-province"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Product Interest *</label>
                      <Select
                        value={inquiryForm.product_interest}
                        onValueChange={(value) => setInquiryForm({ ...inquiryForm, product_interest: value })}
                      >
                        <SelectTrigger className="input-focus" data-testid="inquiry-product">
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HomeLyph Pro 500">HomeLyph Pro 500</SelectItem>
                          <SelectItem value="HomeLyph Compact 300">HomeLyph Compact 300</SelectItem>
                          <SelectItem value="HomeLyph Commercial 1000">HomeLyph Commercial 1000</SelectItem>
                          <SelectItem value="Not sure - need recommendation">Not sure - need recommendation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Budget *</label>
                        <Select
                          value={inquiryForm.budget}
                          onValueChange={(value) => setInquiryForm({ ...inquiryForm, budget: value })}
                        >
                          <SelectTrigger className="input-focus" data-testid="inquiry-budget">
                            <SelectValue placeholder="Select budget range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Under $2,000">Under $2,000</SelectItem>
                            <SelectItem value="$2,000 - $3,000">$2,000 - $3,000</SelectItem>
                            <SelectItem value="$3,000 - $5,000">$3,000 - $5,000</SelectItem>
                            <SelectItem value="Over $5,000">Over $5,000</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">Timeline *</label>
                        <Select
                          value={inquiryForm.timeline}
                          onValueChange={(value) => setInquiryForm({ ...inquiryForm, timeline: value })}
                        >
                          <SelectTrigger className="input-focus" data-testid="inquiry-timeline">
                            <SelectValue placeholder="When do you need it?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ASAP">ASAP</SelectItem>
                            <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                            <SelectItem value="1-3 months">1-3 months</SelectItem>
                            <SelectItem value="Just researching">Just researching</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Additional Information (optional)</label>
                      <Textarea
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                        placeholder="Tell us more about your needs..."
                        rows={4}
                        className="input-focus"
                        data-testid="inquiry-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#003456] hover:bg-[#001d30] text-white py-6 btn-scale"
                      data-testid="inquiry-submit"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      ) : (
                        <>
                          Submit Inquiry <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
